import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Panel from "../../components/brand/Panel";
import {
  createBudgetTemplate,
  getBudgetLineItems,
  getBudgetTemplates,
  updateBudgetTemplate,
} from "../../api/financeApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";

let uidCounter = 0;
const nextUid = () => {
  uidCounter += 1;
  return `section-${uidCounter}`;
};

const DEFAULT_ESTATE_SECTION = {
  name: "Estate Charge",
  section_type: "estate",
  line_item_ids: [],
};

function withUid(section) {
  return { _uid: section._uid || nextUid(), ...section };
}

function normalizeTemplate(data) {
  if (!data) return null;
  const sections = (data.sections || []).map((section) =>
    withUid({
      ...section,
      line_item_ids: (section.lines || []).map((line) => line.line_item_id),
    })
  );
  return { ...data, sections };
}

const MECHANISM_OPTIONS = [
  { value: "equal_shares", label: "Equal shares" },
  { value: "floor_area", label: "Floor area" },
  { value: "discretionary", label: "Discretionary" },
];

const mechanismLabel = (value) =>
  MECHANISM_OPTIONS.find((option) => option.value === value)?.label || "Equal shares";

function sectionsToPayload(sections) {
  return sections.map((section, index) => ({
    id: section.id,
    name: section.name,
    section_type: section.section_type,
    mechanism: section.mechanism || "equal_shares",
    sort_order: index,
    line_item_ids: section.line_item_ids || [],
  }));
}

export default function BudgetFormatEditor({ propertyId, propertyName, selectedBudget, budgetId, onSaved, onMessage, onError }) {
  const [loading, setLoading] = useState(true);
  const readOnly = selectedBudget?.status === "published";
  const [saving, setSaving] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [pendingDelete, setPendingDelete] = useState(null);

  const toggleExpanded = (uid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const groupedLineItems = useMemo(() => {
    const groups = {};
    lineItems.forEach((item) => {
      const key = item.category || "Other";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [lineItems]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      onError?.("");
      const [templatesRes, itemsRes] = await Promise.all([
        getBudgetTemplates(propertyId),
        getBudgetLineItems(),
      ]);
      setLineItems(itemsRes.data.results || []);
      const templates = templatesRes.data.results || [];
      if (templates.length > 0) {
        const normalized = normalizeTemplate({
          ...templates[0],
          name: templates[0].name || `${propertyName || "Property"} budget format`,
        });
        if (!normalized.sections.some((s) => s.section_type === "estate")) {
          normalized.sections = [withUid({ ...DEFAULT_ESTATE_SECTION }), ...normalized.sections];
        }
        setDraft(normalized);
        const estate = normalized.sections.find((s) => s.section_type === "estate");
        setExpanded(new Set(estate ? [estate._uid] : []));
      } else {
        const estate = withUid({ ...DEFAULT_ESTATE_SECTION });
        setDraft({
          name: `${propertyName || "Property"} budget format`,
          sections: [estate],
        });
        setExpanded(new Set([estate._uid]));
      }
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to load budget format."));
    } finally {
      setLoading(false);
    }
  }, [propertyId, propertyName, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const updateSection = (index, patch) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section, idx) =>
        idx === index ? { ...section, ...patch } : section
      ),
    }));
  };

  const toggleLineItem = (sectionIndex, lineItemId) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section, idx) => {
        if (idx !== sectionIndex) return section;
        const ids = new Set(section.line_item_ids || []);
        if (ids.has(lineItemId)) ids.delete(lineItemId);
        else ids.add(lineItemId);
        return { ...section, line_item_ids: [...ids] };
      }),
    }));
  };

  const addBlock = () => {
    const block = withUid({
      name: "",
      section_type: "block",
      mechanism: "equal_shares",
      line_item_ids: [],
    });
    setDraft((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          ...block,
          name: `Block Charge ${
            prev.sections.filter((s) => s.section_type === "block").length + 1
          }`,
        },
      ],
    }));
    setExpanded((prev) => new Set([...prev, block._uid]));
  };

  const removeSection = (index) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, idx) => idx !== index),
    }));
  };

  const confirmDeleteSection = () => {
    if (pendingDelete == null) return;
    removeSection(pendingDelete.index);
    setPendingDelete(null);
  };

  const handleSave = async () => {
    if (budgetId && String(selectedBudget?.id) !== String(budgetId)) {
      onError?.("Wait for the selected budget to finish loading before saving.");
      return;
    }
    const estate = draft?.sections?.find((s) => s.section_type === "estate");
    if (!estate) {
      onError?.("Budget format must include an Estate Charge section.");
      return;
    }
    if (!(estate.line_item_ids || []).length) {
      onError?.("The Estate Charge has no budget line items selected.");
      return;
    }
    const unnamedBlock = draft.sections.find(
      (s) => s.section_type === "block" && !(s.name || "").trim()
    );
    if (unnamedBlock) {
      onError?.("Every Block Charge needs a name.");
      return;
    }

    try {
      setSaving(true);
      onError?.("");
      const payload = {
        name: draft.name,
        sections: sectionsToPayload(draft.sections),
        ...(budgetId && selectedBudget ? { budget_id: selectedBudget.id, budget_row_version: selectedBudget.row_version } : {}),
      };

      let res;
      if (draft.id) {
        res = await updateBudgetTemplate(propertyId, draft.id, {
          ...payload,
          row_version: draft.row_version,
        });
      } else {
        res = await createBudgetTemplate(propertyId, payload);
      }
      const normalized = normalizeTemplate(res.data);
      setDraft(normalized);
      setExpanded(new Set((normalized.sections || []).map((s) => s._uid)));
      await onSaved?.();
      onMessage?.(budgetId ? "Budget format and selected budget saved." : "Budget format saved.");
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        onError?.("Budget format was changed elsewhere. Reloading.");
        await load();
        await onSaved?.();
      } else {
        onError?.(getApiErrorMessage(e, "Failed to save budget format."));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Panel title="Budget format" noBodyPadding={false} sx={{ mb: 2 }}>
        <Skeleton height={180} />
      </Panel>
    );
  }

  if (!draft) return null;

  const estateIndex = draft.sections.findIndex((s) => s.section_type === "estate");
  const blockSections = draft.sections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.section_type === "block");

  return (
    <Panel
      title="Budget format"
      noBodyPadding={false}
      sx={{ mb: 2 }}
      right={
        <Button size="small" variant="contained" disabled={saving || readOnly} onClick={handleSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
          {saving ? "Saving…" : "Save format"}
        </Button>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define the structure for this property: an Estate Charge shared by all units, plus any
        Block Charges with their own budget lines. Saving also updates the selected editable budget,
        preserving existing amounts and allocations. The format is used for future budgets.
      </Typography>

      {readOnly && <Alert severity="info" sx={{ mb: 2 }}>This budget is published. Select an editable budget to change its structure.</Alert>}
      <TextField
        fullWidth
        size="small"
        label="Format name"
        value={draft.name}
        onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
        sx={{ mb: 2 }}
      />

      {estateIndex >= 0 && (
        <Accordion
          expanded={expanded.has(draft.sections[estateIndex]._uid)}
          onChange={() => toggleExpanded(draft.sections[estateIndex]._uid)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography fontWeight={600}>Estate Charge</Typography>
              <Chip
                size="small"
                label={`${(draft.sections[estateIndex].line_item_ids || []).length} line item(s)`}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Costs shared across the whole property. Every unit pays a share based on the property
              service charge mechanism.
            </Typography>
            <LineItemPicker
              groupedLineItems={groupedLineItems}
              selectedIds={draft.sections[estateIndex].line_item_ids || []}
              onToggle={(lineItemId) => toggleLineItem(estateIndex, lineItemId)}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, mt: 2 }}>
        Block Charges
      </Typography>

      {blockSections.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Simple sites may only need an Estate Charge. Add block charges for buildings or areas with
          separate costs (e.g. per-building electricity).
        </Alert>
      )}

      {blockSections.map(({ section, index }) => (
        <Accordion
          key={section._uid}
          expanded={expanded.has(section._uid)}
          onChange={() => toggleExpanded(section._uid)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                pr: 1,
                width: "100%",
              }}
            >
              <Typography fontWeight={600}>
                {(section.name || "").trim() || "Untitled block charge"}
              </Typography>
              <Chip size="small" label={mechanismLabel(section.mechanism)} />
              <Chip
                size="small"
                variant="outlined"
                label={`${(section.line_item_ids || []).length} line item(s)`}
              />
              <Box
                component="span"
                role="button"
                aria-label="Remove block charge"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete({ index, name: section.name });
                }}
                onFocus={(e) => e.stopPropagation()}
                sx={{ ml: "auto", display: "inline-flex" }}
              >
                <IconButton size="small" color="error" component="span">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
              <TextField
                size="small"
                label="Block Charge Name"
                value={section.name}
                onChange={(e) => updateSection(index, { name: e.target.value })}
                sx={{ minWidth: 220, flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Mechanism</InputLabel>
                <Select
                  label="Mechanism"
                  value={section.mechanism || "equal_shares"}
                  onChange={(e) => updateSection(index, { mechanism: e.target.value })}
                >
                  {MECHANISM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <LineItemPicker
              groupedLineItems={groupedLineItems}
              selectedIds={section.line_item_ids || []}
              onToggle={(lineItemId) => toggleLineItem(index, lineItemId)}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 2 }}>
              <Button variant="contained" size="small" disabled={saving || readOnly} onClick={handleSave}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                {saving ? "Saving…" : "Save format"}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Saves this block charge and all other changes to the budget format.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addBlock} disabled={saving || readOnly}>
          Add block charge
        </Button>
        <Button variant="contained" disabled={saving || readOnly} onClick={handleSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
          {saving ? "Saving…" : "Save format"}
        </Button>
      </Box>

      <Dialog open={pendingDelete != null} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete block charge?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Delete "${(pendingDelete?.name || "").trim() || "this block charge"}"? Its budget line selections will be removed from the format. This takes effect when you save the format.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteSection}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
}

function LineItemPicker({ groupedLineItems, selectedIds, onToggle }) {
  if (groupedLineItems.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No budget line items in the catalogue.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { md: "1fr 1fr" } }}>
      {groupedLineItems.map(([category, items]) => (
        <Box key={category}>
          <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>
            {category}
          </FormLabel>
          <FormGroup>
            {items.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => onToggle(item.id)}
                  />
                }
                label={item.name}
              />
            ))}
          </FormGroup>
        </Box>
      ))}
    </Box>
  );
}
